// 75 §4 — THE ONE DEFINITION of what a collaborator's lifecycle state means
// for the UI (ruling A.8, 2026-08-11). This tree has paid twice in a fortnight
// for spelling a predicate inline at every site (OPEN_ST's four spellings,
// the mention rule's two silently-disagreeing copies) — so every picker and
// every greyed render resolves through here, and nothing else states the rule.
//
// The two questions, and they are different questions (brief 75 §4):
//
//   "may this person be CHOSEN for new work"  → isChoosable / choosableCo.
//     ARCHIVE hides a person from every picker. SUSPEND deliberately does
//     not — it "keeps the person out and changes nothing else" (A.8), so a
//     suspended colleague still receives tasks, events and mentions; they
//     simply cannot log in until reinstated.
//
//   "did this person do this"                 → isArchivedCo.
//     A record that already names an archived person KEEPS naming them,
//     greyed (InitialsBadge dims itself on `person.status`), EXCEPT time
//     entries, which render normally because the firm still invoices them.
//     Display sites therefore keep the FULL roster — filtering the roster
//     before an id lookup is how a past assignment silently vanishes, and
//     how a fee re-prices to zero (lib/proformaCompute's makeEffR returns 0
//     for an id it cannot find).
//
//   "may this person be BECOME"               → isActiveCo.
//     The view-as pickers only: the server refuses to simulate a non-active
//     collaborator (/api/auth/simulate), so the picker does not offer one.
//
// `status` is absent on rows cached before the migration (lx:co boot
// fallback); absent means active — the safe direction for every question.

type CoLike = { status?: string | null } | null | undefined;

export const isArchivedCo = (c: CoLike) => !!c && c.status === "archived";

export const isChoosable = (c: CoLike) => !!c && c.status !== "archived";

export const isActiveCo = (c: CoLike) => !!c && (c.status ?? "active") === "active";

/** The options list every roster picker maps over. `keepIds` pins the ids a
 *  record ALREADY carries (a select's current value, a MultiSelect's `sel`):
 *  an archived person stays offered there, because filtering them out of an
 *  EDITOR does not un-choose them — it silently re-points the field on the
 *  next save (an unmatched <select> value renders the FIRST option, and a
 *  MultiSelect chip whose option vanished renders nothing at all). */
export function choosableCo<T>(
  list: T[] | null | undefined,
  keepIds?: ReadonlyArray<number | string | null | undefined> | null,
): T[] {
  const keep = keepIds && keepIds.length ? new Set(keepIds.map(Number)) : null;
  return (list || []).filter((c) => {
    const row = c as { id?: number | string | null; status?: string | null } | null;
    return isChoosable(row) || (keep != null && keep.has(Number(row?.id)));
  });
}

/** A record whose ONLY person (or people) is archived — the person-shaped
 *  hole A.8 earmarks with a `?` badge. Never applied to time entries. */
export function soleArchived(people: CoLike[] | null | undefined): boolean {
  const present = (people || []).filter(Boolean);
  return present.length > 0 && present.every(isArchivedCo);
}
